import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import MetricsCard from './components/MetricsCard';
import AIInsightsPanel from './components/AIInsightsPanel';
import RevenueChart from './components/RevenueChart';
import QuickActions from './components/QuickActions';
import RecentTransactions from './components/RecentTransactions';
import PendingItems from './components/PendingItems';
import CashFlowChart from './components/CashFlowChart';
import FinancialPerformancePanel from './components/FinancialPerformancePanel';
import { getChatCompletion } from '../../services/openaiService';
import Icon from '../../components/AppIcon';

const Dashboard = () => {
  const { userProfile, isAuthenticated } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  
  // Live data states
  const [metricsData, setMetricsData] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // AI Chat Window State
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  // Initialize AI chat with dynamic message based on user data
  useEffect(() => {
    if (isAuthenticated && userProfile?.full_name) {
      const welcomeMessage = {
        id: 1,
        type: 'assistant',
        message: `Hello ${userProfile?.full_name || 'there'}! I'm your AI Financial Assistant. I can help you analyze your live financial data, provide insights on cash flow, revenue trends, and answer questions about your business finances. What would you like to explore today?`,
        timestamp: new Date()
      };
      setChatMessages([welcomeMessage]);
    } else {
      // Generic message for users not yet authenticated
      const genericMessage = {
        id: 1,
        type: 'assistant',
        message: 'Hi! I\'m your AI Financial Assistant. How can I help you with your business finances today?',
        timestamp: new Date()
      };
      setChatMessages([genericMessage]);
    }
  }, [isAuthenticated, userProfile?.full_name]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Load real financial metrics from Supabase
  useEffect(() => {
    const loadDashboardMetrics = async () => {
      if (!isAuthenticated || !userProfile?.organization_id) {
        setMetricsLoading(false);
        return;
      }

      try {
        setMetricsLoading(true);
        setError(null);

        // Fetch invoice data for revenue metrics
        const { data: invoiceData, error: invoiceError } = await supabase
          ?.from('invoices')
          ?.select('total_amount, status, created_at')
          ?.eq('organization_id', userProfile?.organization_id);

        if (invoiceError) {
          console.error('Error loading invoice data:', invoiceError);
          setError('Failed to load revenue data');
          return;
        }

        // Fetch bills data for expense metrics
        const { data: billData, error: billError } = await supabase
          ?.from('bills')
          ?.select('total_amount, status, created_at')
          ?.eq('organization_id', userProfile?.organization_id);

        if (billError) {
          console.error('Error loading bill data:', billError);
          setError('Failed to load expense data');
          return;
        }

        // Calculate metrics from real data
        const totalRevenue = invoiceData?.reduce((sum, invoice) => {
          return sum + (invoice?.status === 'paid' ? (invoice?.total_amount || 0) : 0);
        }, 0) || 0;

        const totalExpenses = billData?.reduce((sum, bill) => {
          return sum + (bill?.status === 'paid' || bill?.status === 'approved' ? (bill?.total_amount || 0) : 0);
        }, 0) || 0;

        const netProfit = totalRevenue - totalExpenses;
        const cashFlow = netProfit; // Simplified calculation

        // Calculate growth percentages (simplified - could be enhanced with historical data)
        const revenueGrowth = totalRevenue > 0 ? 'N/A' : '0%';
        const profitGrowth = netProfit > 0 ? 'N/A' : '0%';
        const expenseGrowth = totalExpenses > 0 ? 'N/A' : '0%';
        const cashFlowGrowth = cashFlow > 0 ? 'N/A' : '0%';

        const calculatedMetrics = [
          {
            title: 'Total Revenue',
            value: `$${totalRevenue?.toLocaleString()}`,
            change: revenueGrowth,
            changeType: totalRevenue > 0 ? 'positive' : 'neutral',
            icon: 'DollarSign',
            trend: totalRevenue > 0
          },
          {
            title: 'Net Profit',
            value: `$${netProfit?.toLocaleString()}`,
            change: profitGrowth,
            changeType: netProfit > 0 ? 'positive' : 'negative',
            icon: 'TrendingUp',
            trend: netProfit > 0
          },
          {
            title: 'Total Expenses',
            value: `$${totalExpenses?.toLocaleString()}`,
            change: expenseGrowth,
            changeType: 'negative',
            icon: 'CreditCard',
            trend: false
          },
          {
            title: 'Cash Flow',
            value: `$${cashFlow?.toLocaleString()}`,
            change: cashFlowGrowth,
            changeType: cashFlow > 0 ? 'positive' : 'negative',
            icon: 'Wallet',
            trend: cashFlow > 0
          }
        ];

        setMetricsData(calculatedMetrics);

      } catch (err) {
        console.error('Error loading dashboard metrics:', err);
        setError('Failed to load dashboard data');
      } finally {
        setMetricsLoading(false);
      }
    };

    loadDashboardMetrics();
  }, [isAuthenticated, userProfile?.organization_id]);

  // Download functionality - now uses real data
  const handleDownloadReport = async (format) => {
    setDownloadLoading(true);
    
    try {
      if (format === 'pdf') {
        await generatePDFReport();
      } else if (format === 'xls') {
        await generateXLSReport();
      }
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download ${format?.toUpperCase()} report. Please try again.`);
    } finally {
      setDownloadLoading(false);
      setShowDownloadModal(false);
    }
  };

  const generatePDFReport = async () => {
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    
    const doc = new jsPDF();
    
    // Header
    doc?.setFontSize(20);
    doc?.text('Financial Dashboard Report', 20, 20);
    
    doc?.setFontSize(12);
    doc?.text(`Generated on: ${formatDate(currentTime)}`, 20, 35);
    doc?.text(`Report Period: ${new Date()?.getFullYear()}`, 20, 45);
    
    // Metrics Overview - use real data
    doc?.setFontSize(16);
    doc?.text('Key Metrics', 20, 65);
    
    const metricsTableData = metricsData?.map(metric => [
      metric?.title,
      metric?.value,
      metric?.change
    ]);
    
    autoTable(doc, {
      head: [['Metric', 'Value', 'Change']],
      body: metricsTableData,
      startY: 75,
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 10 }
    });
    
    // Note about data source
    doc?.setFontSize(10);
    doc?.text('Generated from live Supabase data', 20, doc?.lastAutoTable?.finalY + 20);
    doc?.text('Generated by AccounTech AI Dashboard', 20, doc?.internal?.pageSize?.height - 20);
    
    doc?.save(`Dashboard_Report_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
  };

  const generateXLSReport = async () => {
    const XLSX = await import('xlsx');
    
    // Create workbook
    const wb = XLSX?.utils?.book_new();
    
    // Metrics Sheet - use real data
    const metricsSheetData = [
      ['AccounTech AI - Dashboard Report'],
      [`Generated: ${formatDate(currentTime)}`],
      [''],
      ['Key Metrics', 'Value', 'Change'],
      ...metricsData?.map(metric => [metric?.title, metric?.value, metric?.change])
    ];
    
    const metricsWS = XLSX?.utils?.aoa_to_sheet(metricsSheetData);
    XLSX?.utils?.book_append_sheet(wb, metricsWS, 'Metrics');
    
    // Note sheet about data source
    const noteData = [
      ['Data Source Information'],
      [''],
      ['This report is generated from live data in your Supabase database.'],
      ['All figures reflect real-time financial information from your organization.'],
      [''],
      [`Report generated: ${new Date()?.toLocaleString()}`],
      [`Organization: ${userProfile?.organization_name || 'Current Organization'}`]
    ];
    
    const noteWS = XLSX?.utils?.aoa_to_sheet(noteData);
    XLSX?.utils?.book_append_sheet(wb, noteWS, 'Data Source');
    
    // Write file
    XLSX?.writeFile(wb, `Dashboard_Report_${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSendMessage = async () => {
    if (!userInput?.trim() || chatLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: userInput?.trim(),
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setUserInput('');
    setChatLoading(true);
    setIsTyping(true);

    try {
      // Create context from current real dashboard data
      const dashboardContext = `
        Current Financial Overview (Live Data):
        - Total Revenue: ${metricsData?.[0]?.value || '$0'}
        - Net Profit: ${metricsData?.[1]?.value || '$0'}
        - Total Expenses: ${metricsData?.[2]?.value || '$0'}
        - Cash Flow: ${metricsData?.[3]?.value || '$0'}
        
        User is on the Dashboard viewing their real-time financial metrics.
      `;

      const aiResponse = await getChatCompletion(userInput, 'accounting');
      
      setIsTyping(false);
      
      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        message: aiResponse,
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
      
    } catch (error) {
      setIsTyping(false);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        message: 'I apologize, but I\'m having trouble processing your request right now. Please try again later.',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const clearChatHistory = () => {
    const welcomeMessage = {
      id: 1,
      type: 'assistant',
      message: `Hello ${userProfile?.full_name || 'there'}! I'm your AI Financial Assistant. I can help you analyze your live financial data, provide insights on cash flow, revenue trends, and answer questions about your business finances. What would you like to explore today?`,
      timestamp: new Date()
    };
    setChatMessages([welcomeMessage]);
  };

  const renderDownloadModal = () => {
    if (!showDownloadModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="Download" size={20} className="mr-2" />
                Export Dashboard Report
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowDownloadModal(false)}
                disabled={downloadLoading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>
          
          <div className="p-6">
            <p className="text-muted-foreground mb-6">
              Export your live dashboard data including real metrics, transactions, and financial summaries from your Supabase database.
            </p>
            
            <div className="space-y-4">
              <Button
                variant="default"
                onClick={() => handleDownloadReport('pdf')}
                disabled={downloadLoading}
                className="w-full flex items-center justify-center space-x-2 h-12"
                iconName="FileText"
                iconPosition="left"
              >
                {downloadLoading ? 'Generating...' : 'Download PDF Report'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => handleDownloadReport('xls')}
                disabled={downloadLoading}
                className="w-full flex items-center justify-center space-x-2 h-12"
                iconName="Sheet"
                iconPosition="left"
              >
                {downloadLoading ? 'Generating...' : 'Download Excel Report'}
              </Button>
            </div>
            
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-1">Report includes real-time data:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Live financial metrics and trends</li>
                    <li>Actual transaction history from database</li>
                    <li>Real revenue and expense calculations</li>
                    <li>Current period summaries and analytics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAIChatWindow = () => {
    if (!showAIChat) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl h-[600px] flex flex-col">
          {/* Chat Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white text-lg">🤖</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">AI Financial Assistant</h3>
                <p className="text-sm text-muted-foreground">
                  {isTyping ? 'Typing...' : 'Online and ready to help'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                iconName="RotateCcw"
                onClick={clearChatHistory}
                title="Clear chat history"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setShowAIChat(false)}
              />
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatMessages?.map(message => (
              <div
                key={message?.id}
                className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message?.type === 'user' ?'bg-primary text-primary-foreground ml-4' :'bg-muted text-foreground mr-4'
                  }`}
                >
                  <p className="text-sm">{message?.message}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {formatTime(message?.timestamp)}
                  </p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-muted text-foreground px-4 py-2 rounded-lg mr-4">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Chat Input */}
          <div className="p-4 border-t border-border">
            <div className="flex space-x-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me about your finances, trends, or get business insights..."
                className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={chatLoading}
              />
              <Button
                variant="default"
                size="sm"
                iconName="Send"
                onClick={handleSendMessage}
                disabled={!userInput?.trim() || chatLoading}
                className="px-4"
              >
                Send
              </Button>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                variant="outline"
                size="xs"
                onClick={() => setUserInput('Analyze my current cash flow')}
                disabled={chatLoading}
              >
                Analyze Cash Flow
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => setUserInput('What are my biggest expenses this month?')}
                disabled={chatLoading}
              >
                Top Expenses
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => setUserInput('Give me insights on revenue trends')}
                disabled={chatLoading}
              >
                Revenue Insights
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => setUserInput('How can I improve my profit margin?')}
                disabled={chatLoading}
              >
                Improve Profit
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show loading state when metrics are loading
  if (metricsLoading) {
    return (
      <>
        <Helmet>
          <title>Dashboard - AccounTech AI</title>
          <meta name="description" content="Comprehensive financial overview and AI-powered insights for strategic business decision-making" />
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <Sidebar 
            isCollapsed={sidebarCollapsed} 
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
          />
          
          <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-center h-64">
                <div className="flex items-center space-x-3">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                  <span className="text-lg text-muted-foreground">Loading your financial dashboard...</span>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  // Show error state if loading failed
  if (error && !isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Dashboard - AccounTech AI</title>
          <meta name="description" content="Comprehensive financial overview and AI-powered insights for strategic business decision-making" />
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <Sidebar 
            isCollapsed={sidebarCollapsed} 
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
          />
          
          <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Icon name="Lock" size={24} className="text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-foreground mb-2">Authentication Required</h2>
                  <p className="text-muted-foreground mb-4">Please sign in to view your financial dashboard.</p>
                  <Button onClick={() => window.location.href = '/login'}>
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - AccounTech AI</title>
        <meta name="description" content="Comprehensive financial overview and AI-powered insights for strategic business decision-making" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 space-y-6">
            {/* Welcome Section with Download Button */}
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-lg p-6 border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-semibold text-foreground mb-2">
                    Welcome back, {userProfile?.full_name || 'User'}! 👋
                  </h1>
                  <p className="text-muted-foreground">
                    {formatDate(currentTime)} • {formatTime(currentTime)}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="hidden md:block text-right mr-4">
                    <p className="text-sm text-muted-foreground">Data from</p>
                    <p className="font-medium text-foreground">Live Database</p>
                  </div>
                  {/* Download Button */}
                  <Button
                    variant="outline"
                    iconName="Download"
                    iconPosition="left"
                    onClick={() => setShowDownloadModal(true)}
                    className="border-primary/30 hover:border-primary hover:bg-primary/5"
                  >
                    Export Reports
                  </Button>
                  {/* AI Chat Toggle Button */}
                  <Button
                    variant="default"
                    iconName="MessageCircle"
                    iconPosition="left"
                    onClick={() => setShowAIChat(true)}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg"
                  >
                    AI Assistant
                  </Button>
                </div>
              </div>
            </div>

            {/* Key Metrics - now with real data */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {metricsData?.map((metric, index) => (
                <MetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  trend={metric?.trend}
                />
              ))}
            </div>

            {/* AI Insights Panel - pass organization data */}
            <AIInsightsPanel 
              organizationId={userProfile?.organization_id}
              refreshTrigger={metricsData}
            />

            {/* Charts Section - pass organization context */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <RevenueChart organizationId={userProfile?.organization_id} refreshTrigger={metricsData} />
              <CashFlowChart organizationId={userProfile?.organization_id} refreshTrigger={metricsData} />
            </div>

            {/* Financial Performance Analysis */}
            <FinancialPerformancePanel 
              organizationId={userProfile?.organization_id}
              refreshTrigger={metricsData}
            />

            {/* Quick Actions */}
            <QuickActions />

            {/* Recent Activity & Pending Items - organization context */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <RecentTransactions />
              <PendingItems organizationId={userProfile?.organization_id} />
            </div>

            {/* Footer */}
            <div className="pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                <p className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} AccounTech AI. All rights reserved.
                </p>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <span>Last sync: {formatTime(currentTime)}</span>
                  <span>•</span>
                  <span>Live data from Supabase</span>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Download Modal */}
        {renderDownloadModal()}

        {/* AI Chat Window Modal */}
        {renderAIChatWindow()}

        {/* Floating AI Assistant Button - Alternative access point */}
        {!showAIChat && (
          <div className="fixed bottom-6 right-6 z-40">
            <Button
              variant="default"
              size="lg"
              iconName="Bot"
              onClick={() => setShowAIChat(true)}
              className="rounded-full w-14 h-14 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 pulse-animation"
              title="Open AI Financial Assistant"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Dashboard;